import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../data/providers/AuthProvider";
import {
  initializeGoogleAds,
  trackGoogleAdsConversion,
} from "../utils/googleAds";

const REDIRECT_DELAY_SECONDS = 5;

const PaymentSuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS);

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      params.get("session_id") || params.get("checkout_session_id") || undefined
    );
  }, [location.search]);

  const redirectTo = user ? "/dashboard/plan" : "/login";

  useEffect(() => {
    initializeGoogleAds();

    const dedupeId = sessionId || `${location.pathname}${location.search}`;
    const sessionStorageKey = `google-ads-conversion:${dedupeId}`;

    if (sessionStorage.getItem(sessionStorageKey)) return;

    const tracked = trackGoogleAdsConversion(sessionId);

    if (tracked) {
      sessionStorage.setItem(sessionStorageKey, "1");
    }
  }, [location.pathname, location.search, sessionId]);

  useEffect(() => {
    if (loading) return;

    setSecondsLeft(REDIRECT_DELAY_SECONDS);

    const intervalId = window.setInterval(() => {
      setSecondsLeft((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    const timeoutId = window.setTimeout(() => {
      navigate(redirectTo, { replace: true });
    }, REDIRECT_DELAY_SECONDS * 1000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [loading, navigate, redirectTo]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
        background:
          "radial-gradient(circle at top, rgba(0,153,255,0.18), transparent 30%), linear-gradient(180deg, #f6f9ff 0%, #ffffff 100%)",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 560,
          borderRadius: 4,
          boxShadow: "0 18px 50px rgba(14,30,253,0.10)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Box
              sx={{
                width: 84,
                height: 84,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, rgba(14,30,253,0.14) 0%, rgba(0,153,255,0.18) 100%)",
              }}
            >
              <CheckCircleRoundedIcon
                sx={{ fontSize: 52, color: "success.main" }}
              />
            </Box>

            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, mb: 1, color: "text.primary" }}
              >
                Pago exitoso
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Gracias por completar tu pago con Convisas. En unos segundos te
                llevaremos de vuelta a tu panel principal.
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                p: 2,
                borderRadius: 3,
                bgcolor: "rgba(14,30,253,0.04)",
                border: "1px solid rgba(14,30,253,0.10)",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {loading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, mt: "-2px" }} />
                    Preparando la redirección...
                  </>
                ) : (
                  <>Redirección automática en {secondsLeft} segundos.</>
                )}
              </Typography>
            </Box>

            <Button
              variant="contained"
              disabled={loading}
              onClick={() => navigate(redirectTo, { replace: true })}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 999,
                fontWeight: 700,
                background:
                  "linear-gradient(90deg, #0E1EFD 0%, #0066FF 60%, #0099FF 100%)",
              }}
            >
              Ir ahora
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PaymentSuccessPage;
